import { useCallback, useEffect, useState } from "react";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";

export type ActorTemplateScope = "platform" | "org" | "override";
export type ActorTemplateSource = "seed" | "overridden" | "user";
export type ReasoningEffort =
  | "none"
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "max";

export interface ActorTemplate {
  avatarSeed: string;
  compactContextGrowthThreshold: number;
  consolidationSchedule: string | null;
  createdAt: string;
  description: string | null;
  heartbeatIntervalS: number | null;
  id: string;
  identityPrompt: string | null;
  isSingleton: boolean;
  /** Recommended role when spawning from an issue: the "lead" template is
   * preselected by the Jira wizard (at most one per org). */
  issueWorkspaceDefault?: "lead" | "member" | null;
  /** Org MCP servers this template attaches. The API always sends an array. */
  mcpServerIds?: string[];
  modelRef: string | null;
  name: string;
  organizationId: string;
  reasoningEffort: ReasoningEffort | null;
  /** "platform" — seeded, immutable row. Edits go through override.
   *  "org"      — user-created. Editable by any org member.
   *  "override" — never returned directly from list/get (merged into platform). */
  scope: ActorTemplateScope;
  /** Non-null for merged platform rows that have an override applied. */
  overrideId: string | null;
  parentTemplateId: string | null;
  /** Provenance hint: "seed" (untouched platform), "overridden" (platform + org override merged), "user" (scope='org'). */
  source: ActorTemplateSource;
  tickIntervalMs: number | null;
  unarmedParkGraceS: number | null;
  updatedAt: string;
}

export interface CreateTemplateInput {
  avatarSeed: string;
  mcpServerIds?: string[];
  compactContextGrowthThreshold?: number;
  consolidationSchedule?: string;
  description?: string;
  heartbeatIntervalS?: number;
  identityPrompt?: string;
  isSingleton?: boolean;
  issueWorkspaceDefault?: "lead" | "member" | null;
  modelRef?: string;
  name: string;
  reasoningEffort?: ReasoningEffort | null;
  tickIntervalMs?: number;
  unarmedParkGraceS?: number;
}

export interface UpdateTemplateInput {
  avatarSeed?: string;
  /** A null clears every attachment; undefined leaves them untouched. */
  mcpServerIds?: string[] | null;
  compactContextGrowthThreshold?: number;
  consolidationSchedule?: string | null;
  description?: string | null;
  heartbeatIntervalS?: number | null;
  identityPrompt?: string | null;
  isSingleton?: boolean;
  issueWorkspaceDefault?: "lead" | "member" | null;
  modelRef?: string | null;
  name?: string;
  reasoningEffort?: ReasoningEffort | null;
  tickIntervalMs?: number | null;
  unarmedParkGraceS?: number | null;
}

export function useTemplates() {
  const [templates, setTemplates] = useState<ActorTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { subscribe } = useDashboardRealtime();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch("/user/templates");
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const data = (await res.json()) as { templates: ActorTemplate[] };
      setTemplates(data.templates);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "Failed to load templates",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const unsubscribe = subscribe(
      (message) => {
        if (message.type === "template_changed") {
          void load();
        }
      },
      { topic: "internalEventTopic" },
    );
    return () => unsubscribe();
  }, [load, subscribe]);

  const createTemplate = useCallback(
    async (input: CreateTemplateInput): Promise<void> => {
      const res = await fetch("/user/templates", {
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const template = (await res.json()) as ActorTemplate;
      setTemplates((prev) => [template, ...prev]);
    },
    [],
  );

  const updateTemplate = useCallback(
    async (id: string, input: UpdateTemplateInput): Promise<void> => {
      const res = await fetch(`/user/templates/${id}`, {
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
        method: "PATCH",
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const updated = (await res.json()) as ActorTemplate;
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
    },
    [],
  );

  const deleteTemplate = useCallback(async (id: string): Promise<void> => {
    const res = await fetch(`/user/templates/${id}`, { method: "DELETE" });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    // REST response shape depends on the target's scope:
    //   Scope='org'      → { status: "ok" }            — row deleted, remove from list.
    //   Scope='platform' → { status: "reverted", template: ResolvedActorTemplate }
    //                       — override row removed but seed row still lives; replace in list.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const body = (await res.json().catch(() => ({}))) as
      | { status: "ok" }
      | { status: "reverted"; template: ActorTemplate | null };
    if (body.status === "reverted" && body.template) {
      const reverted = body.template;
      setTemplates((prev) => prev.map((t) => (t.id === id ? reverted : t)));
    } else {
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    }
  }, []);

  return {
    createTemplate,
    deleteTemplate,
    error: loadError,
    loading,
    reload: load,
    templates,
    updateTemplate,
  };
}
